(function () {
  'use strict';
  if (customElements.get('clic-collection-list')) return;
  customElements.define('clic-collection-list', class extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onKey = (event) => { if (event.code === 'Space' && event.target.matches('[data-list-filters]')) { event.preventDefault(); event.target.click(); } };
      this.onHistory = () => {
        if (this.pendingRequest) this.pendingRequest.clicListCancelled = true;
        this.pending = false;
        this.sync();
      };
      if (history.state?.searchParams == null) history.replaceState({ ...history.state, searchParams: location.search.slice(1) }, '', location.href);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      window.addEventListener('popstate', this.onHistory);
      customElements.whenDefined('collection-filters').then(() => {
        if (!this.isConnected) return;
        this.host = document.querySelector('collection-filters');
        this.container = this.host?.closest('[data-section-id]');
        if (!this.container) return;
        this.originalSubmit = this.host.onSubmitHandler;
        this.submitWrapper = (event) => {
          if (!event?.clicListCancelled) return this.originalSubmit.call(this.host, event);
        };
        this.host.onSubmitHandler = this.submitWrapper;
        this.originalRender = this.host.renderSection;
        this.renderWrapper = (...args) => {
          this.pending = false;
          // Capture before the vendor's synchronous cache path can open the drawer.
          this.closeAfter = !this.host.classList.contains('collection__filters--visible');
          return this.originalRender.apply(this.host, args);
        };
        this.host.renderSection = this.renderWrapper;
        this.observer = new MutationObserver(() => {
          const loading = this.container.classList.contains('is-loading');
          this.loading = loading;
          this.sync();
          if (!loading && this.closeAfter) {
            this.closeAfter = false;
            // Close from a FRESH TIMER, never rAF (mobile scroll-freeze fix,
            // 2026-09-24): after a render the vendor re-adds the drawer's
            // visible class and dispatches `theme:scroll:lock`, which theme.js
            // applies inside a setTimeout(0) queued at render time — and that
            // timer CANCELS any pending `theme:scroll:unlock`. A rAF here ran
            // before it, so hideFiltersDrawer()'s unlock was cancelled and the
            // page stayed locked with the drawer closed (every list tile tap,
            // chip remove and "Clear all" on a phone). A timer queued now is
            // ordered after the vendor's. Same pattern as clic-filter-set.js.
            setTimeout(() => {
              if (this.container.classList.contains('is-loading')) return;
              this.host.hideFiltersDrawer?.();
              this.ensureUnlocked();
            }, 50);
          }
        });
        this.observer.observe(this.container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        this.sync();
      });
    }
    // The vendor's unlock is itself delayed 300ms; check after it. If the page
    // is still scroll-locked (theme.js stamps `data-scroll-locked` on <html>)
    // with the drawer closed and no other overlay open, unlock it ourselves.
    // The theme's unlock handler re-checks the overlay classes, so this can't
    // unlock a cart drawer / modal / search that opened meanwhile.
    ensureUnlocked() {
      setTimeout(() => {
        if (this.host?.classList.contains('collection__filters--visible')) return;
        if (!document.documentElement.hasAttribute('data-scroll-locked')) return;
        const overlays = ['js-drawer-open', 'js-drawer-open-cart', 'search-opened', 'modal--is-visible', 'js-quick-view-visible'];
        if (overlays.some((cls) => document.body.classList.contains(cls))) return;
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }, 450);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.host?.renderSection === this.renderWrapper) this.host.renderSection = this.originalRender;
      if (this.host?.onSubmitHandler === this.submitWrapper) this.host.onSubmitHandler = this.originalSubmit;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      window.removeEventListener('popstate', this.onHistory);
    }
    criteria(tile) {
      try { return JSON.parse(tile.dataset.listFilters).filter(f => /^filter\./.test(f.param) && Array.isArray(f.values) && f.values.length); } catch { return []; }
    }
    selected(criteria, params) {
      return criteria.length > 0 && criteria.every(f => f.values.every(v => params.getAll(f.param).includes(v)));
    }
    toggle(criteria, current) {
      const params = new URLSearchParams(current);
      const remove = this.selected(criteria, params);
      criteria.forEach(f => {
        const values = new Set(params.getAll(f.param));
        f.values.forEach(v => remove ? values.delete(v) : values.add(v));
        params.delete(f.param); values.forEach(v => params.append(f.param, v));
      });
      params.delete('page');
      return params;
    }
    sync() {
      const params = new URLSearchParams(location.search);
      const form = this.host?.querySelector('[data-collection-filters-form]');
      // Keep active values not rendered by the set's whitelist in the vendor form.
      // Active chips still come from Shopify's collection.filters on every render.
      if (form && !this.pending) {
        params.forEach((value, name) => {
          if (!name.startsWith('filter.')) return;
          const exists = [...form.elements].some(e => e.name === name && e.value === value);
          if (!exists) {
            const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value;
            input.dataset.listCarry = ''; form.appendChild(input);
          }
        });
      }
      this.querySelectorAll('[data-list-filters]').forEach(tile => {
        const criteria = this.criteria(tile);
        const selected = this.selected(criteria, params);
        tile.setAttribute('aria-pressed', String(selected));
        tile.setAttribute('aria-disabled', String(Boolean(this.loading)));
        tile.href = `${location.pathname}?${this.toggle(criteria, params)}`;
        const arrow = tile.querySelector('.clic-collection-list__arrow');
        const text = selected ? '✓' : '→';
        if (arrow && arrow.textContent !== text) arrow.textContent = text;
      });
    }
    onClick(event) {
      const tile = event.target.closest('[data-list-filters]');
      if (!tile || !this.contains(tile) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (this.loading || this.container?.classList.contains('is-loading')) { event.preventDefault(); return; }
      const criteria = this.criteria(tile);
      if (!criteria.length || !this.host?.renderSection) return;
      event.preventDefault();
      const params = new URLSearchParams(location.search);
      const form = this.host.querySelector('[data-collection-filters-form]');
      // Include pending drawer/desktop choices, including a pending sort. Remove
      // only represented fields; unknown URL filters and unrelated query params stay.
      if (form) {
        const names = new Set([...form.elements].map(e => e.name).filter(n => n.startsWith('filter.') || n === 'sort_by'));
        names.forEach(n => params.delete(n));
        new FormData(form).forEach((v, k) => { if (names.has(k) && v !== '' && !params.getAll(k).includes(String(v))) params.append(k, String(v)); });
      }
      const next = this.toggle(criteria, params);
      if (!form || !this.host.debouncedSubmitEvent) { this.host.renderSection(next.toString(), event); return; }
      // Submit through the SAME debouncer as the sidebar so rapid sidebar→tile
      // clicks cancel the pending submission instead of racing two requests.
      this.pending = true;
      form.querySelectorAll('[data-list-carry]').forEach(input => input.remove());
      [...form.elements].forEach(input => {
        if (!input.name?.startsWith('filter.')) return;
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = next.getAll(input.name).includes(input.value);
        else input.value = next.get(input.name) || '';
      });
      const represented = new URLSearchParams(new FormData(form));
      next.forEach((value, name) => {
        if (name === 'section_id' || name === 'page' || represented.getAll(name).includes(value)) return;
        const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value;
        input.dataset.listCarry = ''; form.appendChild(input);
      });
      this.pendingRequest = { preventDefault() {}, clicListCancelled: false };
      this.host.debouncedSubmitEvent(this.pendingRequest);
    }
  });
})();
